import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import moment from 'moment';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { NGXLogger } from 'ngx-logger';
import { ServicesProvided } from 'src/app/case/model/services-provided';
import { BillableService } from 'src/app/core/service/billable.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { GridEventHandler } from 'src/app/core/model/grid-settings';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { TranslateService } from '@ngx-translate/core';
import { MemberService } from 'src/app/core/service/member.service';
import { MemberEligibility } from 'src/app/member/model/member';
import { SettingService } from 'src/app/core/service/setting.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { IntlService } from '@progress/kendo-angular-intl';

@Component({
  selector: 'app-task-detail-services-provided',
  templateUrl: './task-detail-services-provided.component.html',
  styleUrls: ['./task-detail-services-provided.component.scss'],
})
export class TaskDetailServicesProvidedComponent implements OnChanges, OnInit
{
  @Input() public servicesProvidedDialogOpened: boolean;
  @Input() public memberId: number;
  @Input() public taskId: string;
  @Input() public caseId: number;
  @Input() public feeScheduleId: number;
  @Input() public feeCategoryId: number;
  @Input() public checkAccumulatorOnly: boolean = false;
  @Input() public outcome: any;

  @Output() public added = new EventEmitter<any>();
  @Output() public cancelling = new EventEmitter();

  public allBillableServices: any[] = [];
  public billableServicesFiltered: any[] = [];
  public searchTerm: string;
  public servicesProvidedForm: any;
  public minServiceDate: Date;
  public maxServiceDate: Date;
  public virtualServices: any = { itemHeight: 30 };
  public viewServices: GridViewManager;
  public viewAccumulators: GridViewManager;
  public rateLoaded = false;
  public accumulatorLoaded = false;
  public serviceForm = [];
  public eligibleMember = undefined;
  public medicaidMissing = undefined;
  public billableServicesRequired = false;
  public blockedNotEligibleServices = false;    // For v1, we are not blocking the services
  public durationLoaded = false;
  public billableDescriptionLoaded = false;
  public windowState = 'maximized';

  private servicesProvided: ServicesProvided;
  private textAmountRequired: string;
  private textServiceMaxExceeded: string;
  private textServiceDurationExceeded: string;

  constructor(
    private fb: UntypedFormBuilder,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private memberService: MemberService,
    private translateService: TranslateService,
    private billableService: BillableService,
    public intl: IntlService,
    private settingService: SettingService) { }

  public get billableServices(): UntypedFormControl
  {
    return this.servicesProvidedForm.get('billableServices');
  }

  public get serviceDate(): UntypedFormControl
  {
    return this.servicesProvidedForm.get('serviceDate');
  }

  public get comments(): UntypedFormControl
  {
    return this.servicesProvidedForm.get('comments');
  }

  public ngOnInit(): void
  {
    this.viewServices = new GridViewManager(99999);
    this.viewAccumulators = new GridViewManager(99999);
    this.translateService.get(['MANUAL-AMOUNT-REQUIRED', 'ACCUMULATOR-MAXIMUM-EXCEEDED', 'ACCUMULATOR-DURATION-EXCEEDED']).subscribe({
      next: (res) =>
      {
        this.textAmountRequired = res['MANUAL-AMOUNT-REQUIRED'];
        this.textServiceMaxExceeded = res['ACCUMULATOR-MAXIMUM-EXCEEDED'];
        this.textServiceDurationExceeded = res['ACCUMULATOR-DURATION-EXCEEDED'];
      }
    });
  }

  public ngOnChanges(): void
  {
    if (this.feeCategoryId === undefined || this.feeScheduleId === undefined)
    {
      return;
    }
    this.initServicesProvided();
    this.buildForm();
  }

  public cancelServicesProvided(): void
  {
    this.servicesProvidedForm.reset();
    this.servicesProvidedDialogOpened = false;
    this.cancelling.emit();
  }

  /**
   * Form submission event handler.
   */
  public createServicesProvided(): void
  {
    // When completing the task with checkAccumulatorOnly, we allow completing the task note but without any selected services.
    if (this.checkAccumulatorOnly) 
    {
      this.saveBillableServices([]);
    }
    else
    {
      const billableIds = this.billableServices.value.map((x: any) => x.id);

      // Check eligibility
      if (this.blockedNotEligibleServices && this.eligibleMember != undefined && !this.eligibleMember && billableIds.length > 0) 
      {
        this.messageHandlerService.displayError(this.medicaidMissing ? 'MEDICAID-NOT-SET' : 'NOT-ELIGIBLE');
        return;
      }

      // Check if currently editing any rows
      if (this.viewServices.data.filter((x: any) => x.editing).length) 
      {
        this.messageHandlerService.displayWarning('COMPLETE-EDIT-OR-CANCEL');
        return;
      }

      // Validations - Make sure that manual is entered if set
      let allValid = true;
      const billableServices = [];
      billableIds.forEach((billableId: number) => 
      {
        const foundService = this.viewServices.data.find((service: any) => service.billableId === billableId);
        if (foundService)
        {
          if (foundService.hasManualAmount && !foundService.totalAmount)
          {
            this.messageHandlerService.displayDirectMessage('error',
              this.textAmountRequired.replace('{{value}}', foundService.name));
            allValid = false;
          }
          else
          {
            billableServices.push({
              "id": foundService.billableId,
              "units": foundService.units,
              "manualAmount": foundService.hasManualAmount ? foundService.totalAmount : null
            })
          }
        }
        else
        {
          // This should not happen
          this.messageHandlerService.displayError('UNPREDICTABLE-BILLABLE-ITEM-ERROR');
          allValid = false;
        }
      });

      // Validate accumulator caps
      if (allValid)
      {
        this.viewAccumulators.data.forEach((x: any) =>
        {
          if (x.validateMaximum) 
          {
            if (x.remainingUnits < 0 || x.remainingAmount < 0)
            {
              this.messageHandlerService.displayDirectMessage('error', this.textServiceMaxExceeded.replace('{{serviceName}}', x.accumulatorName));
              allValid = false;
            }
            if (x.durationExceeded) 
            {
              this.messageHandlerService.displayDirectMessage('error',
                this.textServiceDurationExceeded.replace('{{serviceName}}', x.accumulatorName)
                  .replace('{{firstServiceDate}}', this.intl.formatDate(this.intl.parseDate(x.firstServiceDate), 'd')));
              allValid = false;
            }
          }
        });
      }

      if (allValid)
      {
        this.saveBillableServices(billableServices);
      }
    }
  }

  /**
   * Service cancel handler
   */
  public serviceCancelHandler(event: GridEventHandler): void
  {
    event.dataItem.editing = false;
    event.sender.closeRow(event.rowIndex);
  }

  /**
   * Services grid row editing event handler.
   */
  public serviceEditHandler(event: GridEventHandler): void
  {
    this.serviceForm[event.dataItem.billableId] = this.fb.group(
      {
        billableId: event.dataItem.billableId,
        units: event.dataItem.units,
        hasManualAmount: event.dataItem.hasManualAmount,
        totalAmount: event.dataItem.totalAmount,
        currentRate: event.dataItem.rate,
        editing: true
      });
    event.sender.editRow(event.rowIndex, this.serviceForm[event.dataItem.billableId]);
    event.dataItem.editing = true;
  }

  /**
   * Add service to grid in preparation for save.
   */
  public serviceSaveHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
    const services = this.viewServices.data;
    const updatedService = services.find(x => x.billableId === this.serviceForm[event.dataItem.billableId].get('billableId').value);
    if (updatedService) 
    {
      const rate = this.serviceForm[event.dataItem.billableId].get('currentRate').value;
      updatedService.units = this.serviceForm[event.dataItem.billableId].get('units').value;
      updatedService.totalAmount = this.serviceForm[event.dataItem.billableId].get('hasManualAmount').value ?
        this.serviceForm[event.dataItem.billableId].get('totalAmount').value : (updatedService.units && rate ? updatedService.units * rate : null);
    }
    this.serviceForm[event.dataItem.billableId].markAsDirty();
    event.dataItem.editing = false;
    this.viewServices.setData(services);
    this.loadAccumulatorsGrid(this.viewServices.data);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public serviceDataStateChange(state: DataStateChangeEvent): void
  {
    this.viewServices.setState(state);
  }

  /**
   * Filters billable services based on user input.
   */
  public filterBillableServices(filter: string): void
  {
    this.searchTerm = filter;
    this.billableServicesFiltered = this.allBillableServices
      .filter(x => x.name.toLowerCase().includes(filter.toLowerCase()));
  }

  public servicesProvidedRequired(): boolean
  {
    return this.billableServicesRequired;
  }

  public hasBillableDescription = (dataItem: any) =>
  {
    return dataItem && dataItem.billableDescription;
  }

  private buildForm(): void
  {
    if (this.servicesProvided === undefined)
    {
      this.initServicesProvided();
    }

    this.servicesProvidedForm = this.fb.group({
      billableServices: [
        { value: this.servicesProvided.billableServices, disabled: false }
      ],
      serviceDate: [{ value: this.servicesProvided.serviceDate, disabled: false }, { validators: [Validators.required] }],
      comments: { value: null, disabled: false }
    });

    // Check for eligibility
    this.setEligibility();

    this.servicesProvidedForm.get('billableServices').valueChanges.subscribe({ next: () => { this.buildSelectedServices(this.serviceDate.value); } });
    this.servicesProvidedForm.get('serviceDate').valueChanges.subscribe({
      next: (serviceDate: any) =>
      {
        if (serviceDate === null || serviceDate === undefined) 
        {
          this.billableServices.disable();
        }
        else
        {
          this.billableServices.enable();
        }
      }
    });

    this.billableService.getBillableServicesByCategoryAndScheduleId(this.feeCategoryId, this.feeScheduleId).subscribe({
      next: (services) =>
      {
        this.allBillableServices = services;
        this.filterBillableServices('');
        // Show warning if no accumulators found
        if (this.checkAccumulatorOnly && services.filter((x: any) => x.accumulatorName).length === 0 && this.servicesProvidedDialogOpened)
        {
          this.messageHandlerService.displayWarning('NO-ACCUMULATOR-FOUND');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
    this.clearSelectedServices();
  }

  private clearSelectedServices(): void
  {
    this.rateLoaded = false;
    this.accumulatorLoaded = false;
    this.viewServices.setData([]);

    this.clearAccumulatorServices();
  }

  private clearAccumulatorServices(): void
  {
    this.viewAccumulators.setData([]);
  }

  private initServicesProvided(): void
  {
    this.minServiceDate = moment().subtract('1', 'year').toDate();
    this.maxServiceDate = moment().toDate();

    this.servicesProvided = new ServicesProvided();
    this.servicesProvided.memberId = this.memberId;
    this.servicesProvided.caseId = this.caseId;
    this.servicesProvided.taskId = this.taskId;
    this.servicesProvided.serviceDate = this.maxServiceDate;
    this.servicesProvided.billableServices = [];
    this.serviceForm = [];
  }

  private buildSelectedServices(serviceDate: any): void
  {
    this.billableDescriptionLoaded = false;
    const billableIds = this.billableServices.value ? this.billableServices.value.map((x: any) => x.id) : [];
    if (billableIds.length > 0)
    {
      if (serviceDate)
      {
        // Updating row individually provide the selected units or manualAmount and would apply the new rate if any
        let currentData = this.viewServices.data;

        this.billableService.getAccumulatorsByBillableServices(this.memberId, serviceDate, billableIds).subscribe({
          next: (data: any) =>
          {
            // If date changes we must reset if annualy frequence because we perform +=
            data.forEach((loadedService: any) =>
            {
              if (loadedService.frequencyCode === 'annually')
              {
                const foundDataIndex = currentData.findIndex(x => x.billableId === loadedService.billableId);
                if (foundDataIndex !== -1)
                {
                  currentData[foundDataIndex].currentUnits = 0;
                  currentData[foundDataIndex].currentAmount = 0;
                }
              }
            });

            data.forEach((loadedService: any) =>
            {
              // The accumulator must match the year, month or week of the selected date
              let processRow = true;
              if (loadedService.year != null) 
              {
                // For week, the need to compare using the iso week year
                if (loadedService.week != null) 
                {
                  processRow = loadedService.year === moment(this.serviceDate.value).isoWeekYear();
                }
                else
                {
                  processRow = loadedService.year === this.serviceDate.value.getFullYear();
                }
              }

              if (processRow && loadedService.month != null)
              {
                processRow = loadedService.month === this.serviceDate.value.getMonth() + 1;
              }

              if (processRow && loadedService.week != null)
              {
                processRow = loadedService.week === moment(this.serviceDate.value).isoWeek();
              }

              if (processRow) 
              {
                const foundDataIndex = currentData.findIndex(x => x.billableId === loadedService.billableId);
                // Add any new selection
                if (foundDataIndex === -1) 
                {
                  loadedService.units = loadedService.informationalOnly ? 0 : 1;
                  loadedService.totalAmount = loadedService.rate && !loadedService.informationalOnly ? loadedService.rate * loadedService.units : null;
                  loadedService.editing = false;
                  currentData.push(loadedService);
                }
                else
                {
                  // Updating current values
                  currentData[foundDataIndex].rate = loadedService.rate;
                  currentData[foundDataIndex].elapsedSinceStart = loadedService.elapsedSinceStart;
                  currentData[foundDataIndex].durationExceeded = loadedService.durationExceeded;
                  currentData[foundDataIndex].firstServiceDate = loadedService.firstServiceDate;

                  if (loadedService.frequencyCode === 'annually')
                  {
                    currentData[foundDataIndex].currentUnits += loadedService.currentUnits;
                    currentData[foundDataIndex].currentAmount += loadedService.currentAmount;
                  }
                  else
                  {
                    currentData[foundDataIndex].currentUnits = loadedService.currentUnits;
                    currentData[foundDataIndex].currentAmount = loadedService.currentAmount;
                  }
                  currentData[foundDataIndex].totalAmount = currentData[foundDataIndex].hasManualAmount ? currentData[foundDataIndex].totalAmount :
                    (currentData[foundDataIndex].units && loadedService.rate ? currentData[foundDataIndex].units * loadedService.rate : null);
                }
              }
            });

            // Remove any deleted services
            currentData = currentData.filter((loadedService: any) => billableIds.includes(loadedService.billableId));

            // Set billable description visibility column
            if (this.billableServices.value) 
            {
              this.billableServices.value.forEach((x: any) =>
              {
                const foundItem = currentData.find((d: any) => d.billableId === x.id);
                if (foundItem)
                {
                  foundItem.billableDescription = x.description;
                  if (x.description !== '') { this.billableDescriptionLoaded = true; }
                }
              });
            }

            // Set data
            this.viewServices.setData(currentData);
            this.loadAccumulatorsGrid(this.viewServices.data);

            // Set column visibilities
            this.rateLoaded = currentData.filter((x: any) => x.rate).length > 0;
            this.accumulatorLoaded = currentData.filter((x: any) => x.frequencyName).length > 0;
          },
          error: error =>
          {
            this.messageHandlerService.displayError(error);
          }
        });
      }
    }
    else
    {
      this.clearSelectedServices();
    }
  }

  private saveBillableServices(billableServices: any): void
  {
    this.servicesProvided.serviceDate = new Date(this.servicesProvidedForm.controls.serviceDate.value.getFullYear(),
      this.servicesProvidedForm.controls.serviceDate.value.getMonth(),
      this.servicesProvidedForm.controls.serviceDate.value.getDate(), 12, 0, 0, 0).toISOString();
    this.servicesProvided.comments = this.comments.value;
    this.servicesProvided.billableServices = billableServices;

    this.billableService.createCaseBillable(this.servicesProvided).subscribe({
      next: () =>
      {
        this.added.emit(this.outcome);
        this.servicesProvidedForm.reset();
        this.servicesProvidedDialogOpened = false;
        if (!this.checkAccumulatorOnly)
        {
          this.messageHandlerService.displaySuccess('SERVICES-PROVIDED-SAVED-SUCCESSFULLY');
        }
      },
      error: error =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError('SERVICES-PROVIDED-SAVE-ERROR');
      }
    });
  }

  private loadAccumulatorsGrid(accumulatorsData: any): void
  {
    this.viewAccumulators.setData(this.billableService.getAccumulatorsData(accumulatorsData, 'accumulatorNameSummary', false));
    this.durationLoaded = this.viewAccumulators.data.filter((x: any) => x.duration).length > 0;
  }

  private setEligibility(): void
  {
    this.eligibleMember = undefined;
    this.medicaidMissing = undefined;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        if (sessionSetting.eligibilityAPIEnabled || this.memberService.eligibilitySimulationMode() != null) 
        {
          this.memberService.checkMemberEligibility(this.memberId).subscribe({
            next: (eligibility: MemberEligibility) =>
            {
              if (eligibility)
              {
                this.eligibleMember = eligibility.eligible != null ? eligibility.eligible : undefined;
                this.medicaidMissing = eligibility.medicaidMissing;
              }
              this.setBillableServicesValidator();
            },
            error: (errorMessage) =>
            {
              this.messageHandlerService.displayError(errorMessage);
            }
          });
        }
        else
        {
          this.setBillableServicesValidator();
        }
      }
    });
  }

  private setBillableServicesValidator(): void
  {
    const servicesValidators = this.checkAccumulatorOnly ||
      (this.blockedNotEligibleServices && this.eligibleMember != undefined && !this.eligibleMember) ? [] : [Validators.required];
    this.servicesProvidedForm.get('billableServices').setValidators(servicesValidators);
    this.servicesProvidedForm.get('billableServices').updateValueAndValidity();
    this.billableServicesRequired = servicesValidators.length > 0;
  }
}
